import React, { useCallback, useContext, useState } from 'react';
import { Checkbox } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import { EntriesContext } from 'containers/forms/Entries';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import ParentFilterContext from './ParentFilterContext';
import CheckboxField from 'components/fields/CheckboxField';
import TextInputField from 'components/fields/TextInputField';

const useTreeItemStyles = makeStyles()((theme) => ({
  root: {
    color: theme.palette.text.secondary,
    '&:hover > $content': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:focus > $content $label, &:hover > $content $label, &$selected > $content $label': {
      backgroundColor: 'transparent',
    },
  },
  content: {
    color: theme.palette.text.secondary,
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    '$expanded > &': {
      fontWeight: theme.typography.fontWeightRegular,
    },
  },
  group: {
    marginLeft: 0,
    '& $content': {
      paddingLeft: theme.spacing(3),
    },
    '& $group': {
      '& $content': {
        paddingLeft: theme.spacing(5),
      },
    },
  },
  expanded: {},
  selected: {},
  label: {
    fontWeight: 'inherit',
    color: 'inherit',
  },
  labelRoot: {
    display: 'grid',
    gridTemplateColumns: '24px minmax(0, 1fr) auto',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    columnGap: '8px',
  },
  labelText: {
    fontWeight: 'inherit',
    width: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  labelTextParent: {
    fontWeight: '300',
    fontSize: '1.5em',
    width: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  entryDescription: {
    width: '100%',
  },
  checkbox: {
    padding: '1px',
    margin: '0px 0px 0px 3px',
    flexShrink: 0,
  },
  rowActions: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    justifySelf: 'end',
  },
  infoIcon: {
    fontSize: '18px',
    flexShrink: 0,
  },
  icon: {
    height: '20px',
    marginRight: '1em',
  },
  entriesIcon: {
    width: '24px',
    height: '24px',
    minWidth: '24px',
    maskPosition: 'center center',
    maskRepeat: 'no-repeat',
    maskSize: '20px',
  },
  iconPlaceholder: {
    width: '24px',
    minWidth: '24px',
  },
}));

interface StyledTreeItemProps {
  labelText: string;
  color?: string;
  bgColor?: string;
  categoryChange?: (event: any) => void;
  handleChildCheckboxChange?: (isChecked: boolean, index: any) => void;
  hideCheckBox?: boolean;
  id?: string | number;
  isParent?: boolean;
  collection?: any;
  isForm?: boolean;
  icon?: string;
  description?: string;
  hasSubEntries?: boolean;
  radioBehavior?: any[];
  checked?: boolean;
  nodeId?: any;
  children?: React.ReactNode;
  className?: string;
}

function StyledTreeItem(props: StyledTreeItemProps) {
  const { classes } = useTreeItemStyles();
  const {
    labelText,
    color,
    bgColor,
    categoryChange,
    hideCheckBox,
    id,
    isParent,
    collection,
    isForm,
    icon,
    description,
    hasSubEntries,
    radioBehavior,
    ...other
  } = props;

  const context = useContext(ParentFilterContext);
  const entriesContext = useContext(EntriesContext);
  const [checked, setChecked] = useState(props.checked);

  const isCheckedInForm = isForm && entriesContext && Object.keys(entriesContext).length !== 0
    ? entriesContext.getList().includes(parseInt(other.nodeId, 10))
    : checked;

  let isThisEntryNotInTopSEO = false;
  let disabled = false;
  if (entriesContext != null && Object.keys(entriesContext).length !== 0) {
    isThisEntryNotInTopSEO = isForm && ((entriesContext.getList().length >= 3 && entriesContext.getList().indexOf(parseInt(other.nodeId, 10)) < 0) && !isCheckedInForm);

    if (radioBehavior) {
      disabled = (radioBehavior.indexOf(other.nodeId) >= 0 && entriesContext.getList().some((id: any) => radioBehavior.map((item: any) => parseInt(item, 10)).includes(id)) && entriesContext.getList().indexOf(parseInt(other.nodeId, 10)) < 0 && !isCheckedInForm);
    }
  }

  const handleCheckboxChange = (event: any) => {
    const checkStatus = event.target.checked;
    setChecked(checkStatus);
    const index = parseInt(String(id), 10);

    if (isParent) {
      context.handleParentCheckboxChange(checkStatus);
      context.checkHandleToggle(event);
    } else {
      const eventEntry = event;
      if (entriesContext && Object.keys(entriesContext).length !== 0) {
        let isTopSEO;
        if (checkStatus) {
          isTopSEO = !entriesContext.addCheckedCheckbox(parseInt(other.nodeId, 10));
        } else {
          entriesContext.removeCheckedCheckbox(parseInt(other.nodeId, 10));
        }
        eventEntry.target.entryId = other.nodeId;
        eventEntry.target.topSEO = isTopSEO;
      }
      if (typeof categoryChange !== 'undefined') {
        categoryChange(eventEntry);
      }
      if (typeof context.handleChildCheckboxChange !== 'undefined') {
        context.handleChildCheckboxChange(checkStatus, index);
      }
    }
  };

  const handleClickItem = useCallback((evt: React.MouseEvent) => {
    if (!isParent || !hasSubEntries) {
      evt.stopPropagation();
      if (!isForm) {
        handleCheckboxChange({ target: { checked: !checked } });
      }
    }
  }, [checked, isParent, isForm, hasSubEntries]);

  const handleDescriptionChange = (event: any) => {
    const eventEntry = event;
    eventEntry.target.entryId = other.nodeId;
    eventEntry.target.linkDescription = event.target.value;
    if (categoryChange) {
      categoryChange(event);
    }
  };

  const showCheckbox = !hideCheckBox && !(hasSubEntries && isForm) && (!isThisEntryNotInTopSEO);

  return (
    <TreeItem
      label={(
        <div>
          <div className={classes.labelRoot}>
            {icon ? (
              <span className={classes.entriesIcon} style={{ WebkitMaskImage: `url('/icons/${icon}.svg')`, WebkitMaskPosition: 'center center', WebkitMaskRepeat: 'no-repeat', WebkitMaskSize: '28px', backgroundColor: `${color}` }} />
            ) : (
              <span className={classes.iconPlaceholder} />
            )}
            {hasSubEntries && isForm && (
              <>
                <Typography variant="body2" className={classes.labelTextParent} style={{ color: `${color}` }}>
                  {labelText}
                </Typography>
              </>
            )}
            {!(hasSubEntries && isForm) && (
              <>
                <Typography
                  variant="body2"
                  className={classes.labelText}
                  style={{ color: `${color}` }}
                  onClick={!isForm ? handleClickItem : undefined}
                >
                  {labelText}
                </Typography>
              </>
            )}
            {(description || showCheckbox) && (
              <div className={classes.rowActions}>
                {description && (
                  <Tooltip title={description}>
                    <InfoIcon className={classes.infoIcon} />
                  </Tooltip>
                )}
                {showCheckbox && isForm && (
                  <CheckboxField
                    name={`entries.${collection?.id}`}
                    valueCheckbox={other.nodeId}
                    onChange={handleCheckboxChange}
                    className={classes.checkbox}
                    disabled={disabled}
                    style={{ color: '#2C367E' }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                )}
                {showCheckbox && !isForm && (
                  <Checkbox
                    edge="start"
                    tabIndex={-1}
                    disableRipple
                    name="entries"
                    value={other.nodeId}
                    className={classes.checkbox}
                    style={{ color: '#2C367E' }}
                    checked={checked}
                    onChange={handleCheckboxChange}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            )}
          </div>
          {isForm && isCheckedInForm && !hideCheckBox && !(hasSubEntries && isForm) && (
            <TextInputField
              className={classes.entryDescription}
              variant="outlined"
              name={`entriesWithInformation.entries_${other.nodeId}`}
              placeholder={`Votre lien avec le sujet ${labelText}`}
              onChange={handleDescriptionChange}
              linkDescription
            />
          )}
        </div>
      )}
      style={{
        '--tree-view-color': color,
        '--tree-view-bg-color': bgColor,
      } as React.CSSProperties}
      classes={{
        root: classes.root,
        content: classes.content,
        expanded: classes.expanded,
        selected: classes.selected,
        group: classes.group,
        label: classes.label,
      }}
      {...other}
    />
  );
}

export default StyledTreeItem;
